import axios from 'axios'

/**
 * Cliente HTTP único del panel.
 *
 * Reglas que viven aquí y en ningún otro sitio:
 *  - la base es `/api/v1`;
 *  - el token de acceso se añade en el interceptor de petición;
 *  - un 401 dispara UN solo refresco (single-flight) y las peticiones que
 *    llegaron mientras tanto esperan en cola y se reintentan al terminar;
 *  - si el refresco falla, se avisa una vez a la sesión y se rechaza todo.
 *
 * Ningún componente ni store llama a `axios` directamente.
 */

export const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`

/** Rutas que jamás deben intentar refrescar: son el propio ciclo de sesión. */
const AUTH_FREE_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/password/']

/**
 * Enganches que la sesión (Pinia) instala al arrancar. Se inyectan en vez de
 * importar el store para no crear un ciclo `store → api → store`.
 * @type {{ getAccessToken: () => string|null, refresh: () => Promise<string|null>, onSessionLost: () => void, getLocale: () => string }}
 */
let hooks = {
  getAccessToken: () => null,
  refresh: async () => null,
  onSessionLost: () => {},
  getLocale: () => 'es',
}

export function configureHttp(next) {
  hooks = { ...hooks, ...next }
}

export const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20_000,
  // El refresh token viaja en cookie httpOnly: sin esto no llega nunca.
  withCredentials: true,
  headers: { Accept: 'application/json' },
})

http.interceptors.request.use((config) => {
  const token = hooks.getAccessToken()
  if (token && !isAuthFreePath(config.url)) {
    config.headers.Authorization = `Bearer ${token}`
  }
  config.headers['Accept-Language'] = hooks.getLocale()
  return config
})

/* ─── Refresco con cola ────────────────────────────────────────────────────── */

/** Promesa en curso del refresco. Compartida por todas las peticiones en vuelo. */
let refreshPromise = null

/**
 * Ejecuta el refresco una sola vez aunque lo pidan diez peticiones a la vez.
 * @returns {Promise<string|null>} nuevo token de acceso, o `null` si no se pudo
 */
function refreshOnce() {
  if (!refreshPromise) {
    refreshPromise = hooks
      .refresh()
      .catch(() => null)
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

function isAuthFreePath(url = '') {
  return AUTH_FREE_PATHS.some((path) => url.startsWith(path))
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    const shouldRefresh =
      error.response?.status === 401 &&
      original &&
      !original.__retriedAfterRefresh &&
      !isAuthFreePath(original.url) &&
      !original.__skipAuthRefresh

    if (!shouldRefresh) return Promise.reject(error)

    original.__retriedAfterRefresh = true
    const token = await refreshOnce()

    if (!token) {
      hooks.onSessionLost()
      return Promise.reject(error)
    }

    original.headers = { ...original.headers, Authorization: `Bearer ${token}` }
    return http(original)
  },
)

/* ─── Utilidades ──────────────────────────────────────────────────────────── */

/**
 * Desempaqueta la respuesta y valida su forma antes de entregarla al store.
 * Un backend que devuelve algo distinto de lo pactado se detecta aquí, no en
 * un `undefined.map` tres capas más arriba.
 *
 * @template T
 * @param {Promise<import('axios').AxiosResponse<T>>} request
 * @param {(data: unknown) => boolean} [guard]
 * @returns {Promise<T>}
 */
export async function unwrap(request, guard) {
  const response = await request
  const data = response.data
  if (guard && !guard(data)) {
    const shapeError = new Error('Respuesta con forma inesperada')
    shapeError.response = { status: 502, data: { message: '' } }
    throw shapeError
  }
  return data
}

/** Guarda para las respuestas paginadas `{ items, nextCursor }` del contrato. */
export function isPage(data) {
  return Boolean(data) && Array.isArray(data.items) && 'nextCursor' in data
}

/** Guarda para respuestas que deben ser un objeto. */
export function isObject(data) {
  return Boolean(data) && typeof data === 'object' && !Array.isArray(data)
}

/** Convierte un objeto en query string omitiendo vacíos, `null` y `undefined`. */
export function toQuery(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  )
}

/** URL absoluta de un recurso de la API (para SSE y descargas). */
export function apiUrl(path) {
  return `${API_BASE_URL}${path}`
}
